import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline'
import dayjs from 'dayjs'

import { cn } from '@/app/utils'
import { toMskCalendarDay } from '@/utils/datetime'

import { monthNames } from '../../../monthpicker/ui/MonthPicker.constants'
import { PickerMode } from '../Calendar.types'

type Props = {
  currentDate: Date
  month: Date
  setMonth: (month: Date) => void
  onDateChange: (date: Date) => void
  navigateByMonth?: boolean
  onTitleClick?: () => void
  disabled?: {
    before?: Date
    after?: Date
  }
  pickerMode?: PickerMode
  disableMonthSwitch?: boolean
}

export const HoursCaption = ({
  currentDate,
  month,
  setMonth,
  onDateChange,
  navigateByMonth = false,
  onTitleClick,
  disabled,
  pickerMode,
  disableMonthSwitch = false
}: Props) => {
  const normalizedDate = toMskCalendarDay(currentDate).startOf('day')
  const navigationUnit = navigateByMonth ? 'month' : 'day'
  const minBoundary = disabled?.before
    ? dayjs(disabled.before).startOf(navigationUnit)
    : null
  const maxBoundary = disabled?.after
    ? dayjs(disabled.after).startOf(navigationUnit)
    : null

  const prevDate = normalizedDate.subtract(1, navigationUnit)
  const nextDate = normalizedDate.add(1, navigationUnit)

  const isPrevDisabledByBoundary = minBoundary
    ? prevDate.isBefore(minBoundary, navigationUnit)
    : false
  const isNextDisabledByBoundary = maxBoundary
    ? nextDate.isAfter(maxBoundary, navigationUnit)
    : false

  const isPrevDisabled = disableMonthSwitch || isPrevDisabledByBoundary
  const isNextDisabled = disableMonthSwitch || isNextDisabledByBoundary

  const handlePrevClick = () => {
    if (navigateByMonth) {
      setMonth(dayjs(month).subtract(1, 'month').toDate())
      return
    }

    onDateChange(prevDate.toDate())
  }

  const handleNextClick = () => {
    if (navigateByMonth) {
      setMonth(dayjs(month).add(1, 'month').toDate())
      return
    }

    onDateChange(nextDate.toDate())
  }

  const dayMonthLabel = normalizedDate.format('D MMMM')
  const isMonthPreview = pickerMode === 'months' || pickerMode === 'calendar'

  const getTitleLabel = () => {
    if (pickerMode === 'months') {
      return 'Месяц'
    }

    if (pickerMode === 'calendar') {
      return monthNames[normalizedDate.month()]
    }

    return dayMonthLabel
  }

  return (
    <div className="flex justify-between pb-12">
      <button
        aria-label={navigateByMonth ? 'Предыдущий год' : 'Предыдущий день'}
        type="button"
        onClick={handlePrevClick}
        disabled={isPrevDisabled}
      >
        <ChevronLeftIcon
          className={cn('h-24 w-24', isPrevDisabled && 'text-grey-30')}
        />
      </button>

      <div className="flex gap-4">
        <button
          aria-label={
            pickerMode === 'months'
              ? 'Открыть выбор дня'
              : 'Открыть выбор месяца'
          }
          className="flex items-center gap-4 font-semibold capitalize text-16"
          type="button"
          onClick={onTitleClick}
        >
          {getTitleLabel()}
          {Boolean(onTitleClick) && (
            <ChevronDownIcon
              className={cn('h-12 w-12', isMonthPreview && 'rotate-180')}
            />
          )}
        </button>
        {pickerMode !== 'hours' && (
          <p className="flex items-center gap-4 font-semibold text-16">
            {normalizedDate.year()}
          </p>
        )}
      </div>

      <button
        aria-label={navigateByMonth ? 'Следующий год' : 'Следующий день'}
        type="button"
        onClick={handleNextClick}
        disabled={isNextDisabled}
      >
        <ChevronRightIcon
          className={cn('h-24 w-24', isNextDisabled && 'text-grey-30')}
        />
      </button>
    </div>
  )
}
